import { toast } from 'react-toastify'
import { AxiosError } from 'axios'
import { Store } from 'redux'

import { IApplicationState } from 'setup/store'

import { localeErrorStatusMessageKeysType } from 'shared/locale/en-US'
import { ICommonError } from 'shared/models/AppError'

import { logout, refreshSession } from 'features/auth/actions'

import { getIsRefreshTokenExpired } from 'components/SessionPolling/utils'

import { LOGOUT_RESPONSE_CODE } from './constants'
import { authExcluding } from './helpers/authExcluding'

export const makeUnauthorizedInterceptor = (store: Store<IApplicationState, any>) => (error: AxiosError) => {
  const state = store.getState()
  const localeErrors = state.locale.data.errorStatusMessage

  const { isAuth } = state.auth.data

  // sessions
  if (isAuth) {
    if (error.response?.data?.code === LOGOUT_RESPONSE_CODE) {
      store.dispatch(logout.success())

      toast.error(localeErrors.access_and_refresh_token_invalid)
    } else if (error.response?.status === 401) {
      const { session } = store.getState().auth.data

      if (session) {
        const isRefreshTokenExpired = getIsRefreshTokenExpired(session.refresh)

        if (isRefreshTokenExpired) {
          store.dispatch(logout.success())
        } else {
          setTimeout(() => store.dispatch(refreshSession()), 0)
        }
      } else {
        store.dispatch(logout.success())
      }
    }
  }

  if (error.request?.responseURL?.includes?.('accounts/login')) {
    const isAuthError = authExcluding(error, store)

    if (isAuthError) {
      throw new Error(JSON.stringify(error.response?.data || ''))
    }
  }

  const hasErrorCode = Boolean(localeErrors[error.response?.data?.code as localeErrorStatusMessageKeysType])
  if (error.response?.data?.fields && !hasErrorCode) {
    if (error?.response?.status === 401) {
      throw new Error(JSON.stringify(error.request?.fields) || '')
    }

    error.response.data.fields.forEach((err: ICommonError) => {
      const text =
        localeErrors[err.code as localeErrorStatusMessageKeysType] || err.detail || localeErrors.defaultMessage
      toast.error(text, { toastId: text })
    })

    throw new Error(JSON.stringify(error.request?.fields) || '')
  }

  if (error.response) {
    if (error.response.status === 401) {
      throw new Error(error.message)
    }

    const errorText =
      localeErrors?.[error.response?.data?.code as localeErrorStatusMessageKeysType] ||
      error.response?.data?.message ||
      error.response?.data?.detail

    if (errorText) {
      toast.error(errorText, { toastId: errorText })
    } else if (error.response.status !== 404) {
      toast.error(localeErrors.defaultMessage, { toastId: localeErrors.defaultMessage })
    }
  }

  throw new Error(JSON.stringify(error.response?.data || ''))
}
